
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { GraduationCap, X } from 'lucide-react';

interface EducationFormProps {
  userId: string;
  onComplete: () => void;
  onCancel: () => void;
}

const EducationForm: React.FC<EducationFormProps> = ({ userId, onComplete, onCancel }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    institution: '',
    degree: '',
    fieldOfStudy: '',
    startDate: '',
    endDate: '',
    isCurrent: false,
    description: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      isCurrent: e.target.checked,
      endDate: e.target.checked ? '' : formData.endDate,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (!userId) {
        toast({
          title: "Error",
          description: "User not authenticated",
          variant: "destructive",
        });
        return;
      }

      if (!formData.institution) {
        toast({
          title: "Required fields missing",
          description: "Institution name is required.",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      const { error } = await supabase
        .from('education')
        .insert({
          job_seeker_id: userId,
          institution: formData.institution,
          degree: formData.degree || null,
          field_of_study: formData.fieldOfStudy || null,
          start_date: formData.startDate || null,
          end_date: formData.isCurrent ? null : formData.endDate || null,
          is_current: formData.isCurrent,
          description: formData.description || null,
        });

      if (error) throw error;

      toast({
        title: "Education added",
        description: "Your education has been successfully added.",
      });

      onComplete();
    } catch (error: any) {
      console.error('Error adding education:', error.message);
      toast({
        title: "Failed to add education",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <GraduationCap className="h-5 w-5 text-[#0EA5E9]" />
          <h3 className="text-lg font-medium text-gray-800">Add Education</h3>
        </div>
        <Button 
          variant="ghost" 
          size="icon"
          onClick={onCancel}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="institution">Institution*</Label>
          <Input
            id="institution"
            name="institution"
            value={formData.institution}
            onChange={handleInputChange}
            className="border-[#0EA5E9]/30"
            required
            placeholder="University or School Name"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="degree">Degree</Label>
            <Input
              id="degree"
              name="degree"
              value={formData.degree}
              onChange={handleInputChange}
              className="border-[#0EA5E9]/30"
              placeholder="e.g., Bachelor's, Master's"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="fieldOfStudy">Field of Study</Label>
            <Input
              id="fieldOfStudy"
              name="fieldOfStudy"
              value={formData.fieldOfStudy}
              onChange={handleInputChange}
              className="border-[#0EA5E9]/30"
              placeholder="e.g., Computer Science"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="startDate">Start Date</Label>
            <Input
              id="startDate"
              name="startDate"
              type="date"
              value={formData.startDate}
              onChange={handleInputChange}
              className="border-[#0EA5E9]/30"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="endDate">End Date</Label>
            <Input
              id="endDate"
              name="endDate"
              type="date"
              value={formData.endDate}
              onChange={handleInputChange}
              className="border-[#0EA5E9]/30"
              disabled={formData.isCurrent}
            />
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <input
            id="isCurrent"
            name="isCurrent"
            type="checkbox"
            checked={formData.isCurrent}
            onChange={handleCheckboxChange}
            className="h-4 w-4 rounded border-gray-300 text-[#0EA5E9] focus:ring-[#0EA5E9]"
          />
          <Label htmlFor="isCurrent" className="cursor-pointer">I'm currently studying here</Label>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            className="border-[#0EA5E9]/30 min-h-[100px]"
            placeholder="Additional information about your education"
          />
        </div>
        
        <div className="flex justify-end space-x-2">
          <Button 
            type="button" 
            variant="outline"
            onClick={onCancel}
            className="border-[#0EA5E9]/30 text-[#0EA5E9]"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            className="bg-[#0EA5E9] text-white hover:bg-[#0EA5E9]/90"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Education'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EducationForm;
